import { useMemo } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { type VtourType } from '@pkgs/components/utils/propertyCardUtils';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { VtourIcon } from '@pkgs/components/components/VtourIcon/VtourIcon';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Button } from '@ev-tech/ui-core-components/Button';

import { ImageIcon } from '@/icons/small/ImageIcon';
import { FloorPlanIcon } from '@/icons/small/FloorPlanIcon';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';

import { TabKeys } from '../MediaTabs/MediaTabs.types';

import styles from './ExposeHeroImage.module.css';

export const ActionButtons = ({
  totalImages,
  hasFloorPlan,
  onBtnClick,
  vTourType,
  focusedImageIndex,
}: {
  totalImages: number;
  hasFloorPlan: boolean;
  vTourType?: VtourType;
  onBtnClick: (arg: TabKeys) => void;
  focusedImageIndex?: number;
}) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { breakpoint } = useBreakpoint();

  const galleryButtonLabel = useMemo(() => {
    if (breakpoint && breakpoint > Breakpoints.TABLET_LANDSCAPE) {
      return totalImages.toString();
    }
    return `${focusedImageIndex}/${totalImages}`;
  }, [breakpoint, focusedImageIndex, totalImages]);

  const galleryButtonIcon = useMemo(() => {
    if (breakpoint && breakpoint > Breakpoints.TABLET_PORTRAIT) {
      return <ImageIcon />;
    }
    return null;
  }, [breakpoint]);

  return (
    <div className={styles.buttonGroup}>
      {hasFloorPlan && (
        <IconButton
          className={styles.floorplanButton}
          icon={<FloorPlanIcon />}
          variant="primaryAlt"
          size="small"
          shape="square"
          label={t('search.expose.propertyDetails.label.floorPlan', {
            count: 1,
          })}
          aria-label={t('search.expose.propertyDetails.label.floorPlan', {
            count: 1,
          })}
          onClick={() => onBtnClick(TabKeys.Floorplans)}
        />
      )}
      {vTourType && (
        <IconButton
          className={styles.vtourButton}
          icon={<VtourIcon type={vTourType} />}
          size="small"
          shape="square"
          variant="primaryAlt"
          label={t('search.general.label.virtualTour')}
          aria-label={t('search.general.label.virtualTour')}
          onClick={() => onBtnClick(TabKeys.VirtualTour)}
        />
      )}
      {totalImages > 0 && (
        <Button
          className={styles.imageGalleryButton}
          icon={galleryButtonIcon}
          iconPosition="left"
          variant="secondaryAlt"
          size="small"
          label={galleryButtonLabel}
          onClick={() => onBtnClick(TabKeys.Photos)}
          aria-label={t('search.expose.propertyDetails.ariaLabel.gallery', { prefix: galleryButtonLabel })}
        />
      )}
    </div>
  );
};
